import axios, { microServices } from './axios';
import { loaders } from './../constants';

const PLATFORM_CONNECTIONS = '/platform-connections';

/**
 * Fetch platform connections for the active authenticated organization account.
 * Routes to de-account (DE) as connections are managed there.
 * @param {boolean} [useOverlay=true] - Whether to display full overlay loader
 */
export const getPlatformConnections = async (useOverlay = true) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}`, {}, {
    loader: useOverlay ? loaders.OVERLAY : loaders.NONE,
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Update platform connection settings.
 */
export const updateConnectionSettings = async (id, settings) => {
  return await axios('put', `${PLATFORM_CONNECTIONS}/${id}/settings`, { settings }, {
    loader: loaders.OVERLAY,
    successMessage: 'Accounting Settings Updated Successfully',
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Toggle platform connection active state.
 */
export const toggleConnectionStatus = async (id, isActive) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${id}/toggle`, { is_active: isActive }, {
    loader: loaders.OVERLAY,
    successMessage: isActive ? 'Integration Enabled' : 'Integration Disabled',
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Trigger manual Contact syncing (Customers/Suppliers).
 * Routes to de-core (DE_CORE) as contact data and sync logic lives there.
 */
export const syncPlatformContacts = async (id, entityType, entityIds, direction = 'push', resolutions = undefined, options = {}) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${id}/sync-contacts`, {
    entity_type: entityType,
    entity_ids: entityIds,
    direction,
    resolutions
  }, {
    loader: options?.loader !== undefined ? options.loader : loaders.NONE,
    successMessage: options?.successMessage !== undefined ? options.successMessage : null,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

export const previewPullContacts = async (id, entityType, entityIds) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${id}/sync-contacts/preview-pull`, {
    entity_type: entityType,
    entity_ids: entityIds
  }, {
    loader: loaders.OVERLAY,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch sync audit logs for a platform connection.
 */
export const getConnectionLogs = async (id, offset, limit, search, sortBy, minify = true) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${id}/logs`, {}, {
    loader: loaders.NONE,
    server: microServices.DE,
    params: {
      t: Date.now(),
      ...(offset !== undefined ? { offset } : {}),
      ...(limit !== undefined ? { limit } : {}),
      ...(search !== undefined && search !== '' ? { search } : {}),
      ...(sortBy !== undefined && sortBy !== '' ? { sortBy } : {}),
      ...(minify !== undefined ? { minify } : {})
    }
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Disconnect (delete) a platform connection.
 */
export const disconnectPlatformConnection = async (id) => {
  return await axios('delete', `${PLATFORM_CONNECTIONS}/${id}`, {}, {
    loader: loaders.OVERLAY,
    successMessage: 'Integration Disconnected Successfully',
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Set platform connection as default.
 */
export const setPlatformConnectionDefault = async (id) => {
  return await axios('put', `${PLATFORM_CONNECTIONS}/${id}/default`, {}, {
    loader: loaders.OVERLAY,
    successMessage: 'Default Connection Updated Successfully',
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch dynamic Accounts list from platform.
 */
export const getPlatformAccounts = async (id) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${id}/accounts`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch dynamic Tax Rates list from platform.
 */
export const getPlatformTaxRates = async (id) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${id}/tax-rates`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch dynamic Products/Services list from platform.
 */
export const getPlatformItems = async (id) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${id}/items`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};



/**
 * Fetch background sync job status.
 */
export const getSyncJobStatus = async (connectionId, jobId) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${connectionId}/sync-status/${jobId}`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};



export const exportInvoicesBulk = async (connectionId, invoiceIds, settingsOverride, force = false) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/export-invoice/bulk`, {
    invoice_ids: invoiceIds,
    settings_override: settingsOverride || undefined,
    force
  }, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

export const exportBillsBulk = async (connectionId, billIds, settingsOverride, force = false) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/export-bill/bulk`, {
    bill_ids: billIds,
    settings_override: settingsOverride || undefined,
    force
  }, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

export const cancelSyncJob = async (connectionId, jobId) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/sync/cancel`, {
    jobId
  }, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Push a purchase order or work order to accounting platform.
 */
export const exportPurchaseOrder = async (connectionId, poUuid) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/export-purchase-order`, {
    po_uuid: poUuid
  }, {
    loader: loaders.NONE,
    successMessage: 'Order push queued successfully',
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

export const getCustomerSyncStatus = async (connectionId, customerId) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${connectionId}/customer-sync-status/${customerId}?t=${Date.now()}`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

export const getPlatformTransactionStatuses = async (connectionId) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${connectionId}/transaction-statuses?t=${Date.now()}`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

export const getPlatformCurrencies = async (connectionId) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${connectionId}/currencies?t=${Date.now()}`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};
